import { fetchTickets } from '../../api/endpoints';
import type { Id, TicketListItem } from '../../api/types';

/**
 * The bounded WINDOW of a project's tickets that the board and the versions view both draw from.
 *
 * ⚠️ Lives in `features/board/` and is imported by `features/versions/` — both are this agent's, and
 * a second copy of the "how many pages, and what do we say when we stop" rule is a second place for
 * it to drift.
 *
 * 🔴 WHY A WINDOW AND NOT "ALL OF THEM". `GET /tickets` caps a page at 100 (`MAX_PAGE_SIZE`) and Or's
 * real project holds 486, so any view that needs to see every ticket at once — a board, a per-version
 * rollup — is making several requests and must decide where to stop. It stops at
 * `TICKET_WINDOW_MAX`.
 *
 * ⭐⭐ AND IT SAYS SO. `complete` is false when the window is smaller than the server's `total`, and
 * every count derived from an incomplete window renders as a FLOOR (`12+`), never as `12`. ⛔ The
 * alternative — quietly showing the first 500 of 812 as though they were all of them — is a
 * confident wrong number, which is worse than an error because nobody questions it.
 */

/** The API's own `MAX_PAGE_SIZE`. Asking for more silently returns fewer. */
export const TICKET_PAGE_SIZE = 100;

/** Five requests. Enough for Or's 486; a deliberate stop rather than an unbounded loop. */
export const TICKET_WINDOW_MAX = 500;

export interface TicketWindow {
  items: TicketListItem[];
  /** The server's count of everything matching, regardless of how much we fetched. */
  total: number;
  /** ⭐ False ⇒ every derived count is a floor and the view must say so. */
  complete: boolean;
  /** How many requests this took — surfaced in the UI's "loaded N of M" line. */
  pages: number;
}

export async function fetchProjectTickets(
  projectId: Id,
  signal?: AbortSignal,
): Promise<TicketWindow> {
  const items: TicketListItem[] = [];
  let total = 0;
  let page = 1;

  // Sequential, not parallel: the pages are offset windows over one ordering, and firing them at
  // once assumes a `total` we have not read yet.
  for (;;) {
    const result = await fetchTickets(
      { projectId, page, per_page: TICKET_PAGE_SIZE, status: null, assignee: null },
      signal,
    );
    total = result.total;
    items.push(...result.items);

    const fetchedEverything = items.length >= result.total;
    const hitTheCap = items.length >= TICKET_WINDOW_MAX;
    // A short page means the server has no more, whatever `total` claimed — a guard against looping
    // forever if the two ever disagree.
    const shortPage = result.items.length < TICKET_PAGE_SIZE;

    if (fetchedEverything || hitTheCap || shortPage) break;
    page += 1;
  }

  return { items, total, complete: items.length >= total, pages: page };
}

/** `12` when the window is complete, `12+` when it is not. Used for every count on both views. */
export function formatCount(count: number, complete: boolean): string {
  return complete ? String(count) : `${count}+`;
}
